import { useMemo } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { buildTombstoneLookup, type TombstoneLookup } from '@bliks-io/ui';
import { apiClient } from '../client';
import { departmentKeys } from '../query-keys';
import { useAuthStore } from '@/stores/auth-store';
import {
  listDepartmentsContract,
  showDepartmentContract,
  type Department,
  type ListDepartmentsResponse,
  type ShowDepartmentResponse
} from '@bliks-io/contracts';

/**
 * Fetch departments list
 *
 * @example
 * ```tsx
 * function DepartmentsList() {
 *   const { data: departments, isLoading } = useDepartments();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!departments) return null;
 *
 *   return <List items={departments} />;
 * }
 * ```
 */
export function useDepartments() {
  const { token } = useAuthStore();

  return useQuery({
    ...departmentsQueryOptions(),
    enabled: !!token
  });
}

/**
 * Query options for the live departments list, shared between the hook and
 * route loaders that prefetch it so both sides stay on the same cache key.
 */
export function departmentsQueryOptions() {
  return queryOptions({
    queryKey: departmentKeys.list(),
    queryFn: async ({ signal }): Promise<Department[]> => {
      const { data } = await apiClient.get<ListDepartmentsResponse>(listDepartmentsContract.path, {
        signal
      });
      return data.data;
    }
  });
}

/**
 * Fetch all departments INCLUDING soft-deleted ones, for resolving stale
 * department refs (e.g. a team or org member pointing at a deleted
 * department). Deleted departments carry a `deletedAt` timestamp so read
 * surfaces can gray them out.
 *
 * Never use this for pickers/selectors — those must only offer live
 * departments via {@link useDepartments}.
 */
export function useDepartmentsForLookup() {
  const { token } = useAuthStore();

  return useQuery({
    ...departmentsForLookupQueryOptions(),
    enabled: !!token
  });
}

/**
 * {@link useDepartmentsForLookup} reduced to an id → tombstone map, for table
 * cells that gray out refs dangling to soft-deleted departments.
 */
export function useDepartmentTombstoneLookup(): TombstoneLookup {
  const { data } = useDepartmentsForLookup();
  return useMemo(() => buildTombstoneLookup(data), [data]);
}

/**
 * Query options for the lookup variant, shared between the hook and route
 * loaders that prefetch the lookup so both sides stay on the same cache key.
 */
export function departmentsForLookupQueryOptions() {
  return queryOptions({
    queryKey: departmentKeys.list({ includeDeleted: true }),
    queryFn: async ({ signal }): Promise<Department[]> => {
      const { data } = await apiClient.get<ListDepartmentsResponse>(
        `${listDepartmentsContract.path}?includeDeleted=true`,
        { signal }
      );
      return data.data;
    }
  });
}

export const departmentQueryOptions = (id: string) =>
  queryOptions({
    queryKey: departmentKeys.detail(id),
    queryFn: async ({ signal }): Promise<Department> => {
      const { data } = await apiClient.get<ShowDepartmentResponse>(
        showDepartmentContract.path.replace(':id', id),
        { signal }
      );
      return data.data;
    }
  });

/**
 * Fetch a single department by ID
 *
 * @example
 * ```tsx
 * function DepartmentDetail({ id }: { id: string }) {
 *   const { data: department, isLoading } = useDepartment(id);
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!department) return <NotFound />;
 *
 *   return <DetailView department={department} />;
 * }
 * ```
 */
export function useDepartment(id: string) {
  const { token } = useAuthStore();

  return useQuery({
    ...departmentQueryOptions(id),
    enabled: !!token && !!id
  });
}
